import type { InputContainerProps } from '../InputContainer/index.ts';
import clsx from 'clsx';
import { PiWarningCircleFill } from 'react-icons/pi';
import { InputContainer } from '../InputContainer/index.ts';
import { useTextInputGroup } from './useTextInputGroup.ts';

export interface TextInputContainerProps extends Pick<
  InputContainerProps,
  'cornerText' | 'error' | 'helpText' | 'label'
> {
  border?: boolean;
  children: React.ReactNode;
  disabled?: boolean;
  focused?: boolean;
  rounded?: 'none' | 'normal' | 'full';
  showErrorIcon?: boolean;
}

export function TextInputContainer({
  border = true,
  children,
  cornerText,
  disabled,
  focused,
  error,
  helpText,
  label,
  rounded = 'normal',
  showErrorIcon = true
}: TextInputContainerProps) {
  const inputGroup = useTextInputGroup();

  return (
    <InputContainer cornerText={cornerText} error={error} helpText={helpText} label={label}>
      <div
        className={clsx(
          // Basic layout
          'relative block w-full',

          // Background color and shadow applied to inset pseudo-element, so shadow blends with border
          'before:pointer-events-none before:absolute before:inset-px before:shadow-sm',
          rounded === 'normal' && 'before:rounded-[calc(var(--radius-md)-1px)]',
          rounded === 'full' && 'before:rounded-[calc(var(--radius-3xl)-1px)]',

          // Focus ring
          border &&
            clsx(
              'after:pointer-events-none after:absolute after:inset-0 after:ring-inset',
              'sm:focus-within:after:ring-2 sm:focus-within:after:ring-primary-500',
              focused ? 'after:ring-2 after:ring-primary-500' : 'after:ring-transparent',
              rounded === 'normal' && 'after:rounded-md',
              rounded === 'full' && 'after:rounded-3xl'
            ),

          // Disabled state
          disabled && 'opacity-50 before:bg-gray-950/5 before:shadow-none',

          // Invalid state
          error && 'before:shadow-red-500/10'
        )}
      >
        <div
          className={clsx(
            'bg-white',
            rounded === 'normal' && 'rounded-md',
            rounded === 'full' && 'rounded-3xl',

            // Border
            border &&
              clsx(
                'overflow-hidden border',
                disabled && 'border-gray-950/20',
                error && 'border-red-500 hover:border-red-500',
                !disabled && !error && 'border-gray-950/10 hover:border-gray-950/20'
              )
          )}
        >
          {children}
        </div>

        {!!error && !inputGroup && showErrorIcon && (
          <div className="pointer-events-none absolute top-0 right-0 flex h-9 items-center pr-3">
            <PiWarningCircleFill className="size-5 text-red-500" />
          </div>
        )}
      </div>
    </InputContainer>
  );
}
